import React, { useContext, useState } from 'react'
import { PageWrapper, TabsV2 } from '../../components'
import { TabsV2Item } from '../../components/TabsV2/TabsV2'
import { toMenuOptions, WemoloAccordion, WemoloSearchableDropdown } from '../../components/Wemolo'
import { ActionsWrapper } from './Actions/ActionsWrapper'
import { BasicConfig } from './BasicConfig'
import { DeviceContext } from './DeviceContext'
import { DeviceInfo } from './DeviceInfo'
import { DeviceMetabase } from './DeviceMetabase'
import { ExpertConfig } from './ExpertConfig'
import { transformDeviceToSelectValue, transformSelectValueToDevice } from './utils/transformSelectValues'
import { VncLiveView, getVncHostIp } from '../TerminalScreenLiveView'
import LocalErrorBoundary from '../../components/LocalErrorBoundary'
import { SENTRYTAGS } from '../../logging'
import { DeviceTypeEnum } from '../../data/enums'
import { AuditTargetType, ChangeLogView } from '../ChangeLog'
import { useFlags } from 'launchdarkly-react-client-sdk'
import styles from './DeviceConfig.module.css'

const LIVE_VIEW_TAB_KEY = 'liveView'
// Terminals and screens expose a VNC stream; scanners use the camera live view instead.
const VNC_LIVE_VIEW_DEVICE_TYPES = [DeviceTypeEnum.TERMINAL, DeviceTypeEnum.DISPLAY]

export const DeviceConfig = () => {
    const deviceContext = useContext(DeviceContext)
    const [activeTab, setActiveTab] = useState('basic')

    const { terminalScreenLiveView } = useFlags()

    const deviceType = deviceContext.getSelectedDeviceType()
    const supportsVncLiveView =
        terminalScreenLiveView && VNC_LIVE_VIEW_DEVICE_TYPES.includes(deviceType as DeviceTypeEnum)

    return (
        <LocalErrorBoundary keyToRerenderSection={deviceContext.selectedDevice?.uuid} tag={SENTRYTAGS.DEVICECONFIG}>
            <PageWrapper>
                <div className={styles.selectorRow}>
                    <div className={styles.selectorDropdown}>
                        <WemoloSearchableDropdown
                            placeholder="Select device"
                            options={toMenuOptions(deviceContext.devices?.map(transformDeviceToSelectValue) || [])}
                            value={deviceContext.selectedDevice?.uuid}
                            onChange={(id) =>
                                deviceContext.setSelectedDevice(
                                    transformSelectValueToDevice(id ? { id, label: id } : null)
                                )
                            }
                        />
                    </div>
                </div>
                <WemoloAccordion
                    key={`device-info-${Boolean(deviceContext.selectedDevice)}`}
                    title="Device Info"
                    defaultExpanded={Boolean(deviceContext.selectedDevice)}
                >
                    <DeviceInfo />
                </WemoloAccordion>

                {deviceContext.selectedDevice && (
                    <TabsV2
                        aria-label="Device configuration"
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        tabs={[
                            {
                                key: 'basic',
                                label: 'Basic Configuration',
                                content: (
                                    <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                                        <BasicConfig
                                            deviceType={deviceContext.getSelectedDeviceType()}
                                            deviceData={deviceContext.deviceData}
                                            selectedDevice={deviceContext.selectedDevice}
                                            editDiffConfig={deviceContext.editDiffConfig}
                                            setDeviceForceUpdateAction={deviceContext.setDeviceForceUpdateAction}
                                            loadingSaveBasicConfig={deviceContext.loadingSaveBasicConfig}
                                            loadingForceUpdate={deviceContext.loadingForceUpdate}
                                        />
                                    </LocalErrorBoundary>
                                )
                            },
                            {
                                key: 'actions',
                                label: 'Actions',
                                content: (
                                    <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                                        <ActionsWrapper />
                                    </LocalErrorBoundary>
                                )
                            },
                            {
                                key: 'metrics',
                                label: 'Device Metrics',
                                lazy: true,
                                content: (
                                    <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                                        <DeviceMetabase />
                                    </LocalErrorBoundary>
                                )
                            },
                            {
                                key: 'expert',
                                label: 'Expert Configuration',
                                content: (
                                    <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                                        <ExpertConfig />
                                    </LocalErrorBoundary>
                                )
                            },
                            {
                                key: 'changelog',
                                label: 'Change log',
                                content: (
                                    <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                                        <ChangeLogView
                                            targetId={deviceContext.selectedDevice?.uuid}
                                            targetType={AuditTargetType.device}
                                        />
                                    </LocalErrorBoundary>
                                )
                            },
                            ...(supportsVncLiveView
                                ? [
                                      {
                                          key: LIVE_VIEW_TAB_KEY,
                                          label: 'Live View',
                                          content: (
                                              <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                                                  <VncLiveView
                                                      ip={getVncHostIp(deviceContext.deviceData)}
                                                      deviceType={deviceType}
                                                      active={activeTab === LIVE_VIEW_TAB_KEY}
                                                  />
                                              </LocalErrorBoundary>
                                          )
                                      } as TabsV2Item
                                  ]
                                : [])
                        ]}
                        renderAll
                    />
                )}
            </PageWrapper>
        </LocalErrorBoundary>
    )
}
